import React, { useState } from 'react'
import { Box, MenuItem, Select } from '@mui/material'
import { useTheme, styled } from '@mui/material/styles'
import { useCurrentDeckContext } from 'components/currentDeckContext/CurrentDeckContext'
import { observeEquipment } from 'hooks'
import './DeckSelector.scss'

export function DeckSelector({ decks, screens, customers, locations, sites }) {
  const [openDropdown, setOpenDropdown] = useState(null) // Track the open dropdown
  const defaultString = '' // Define defaultString as an empty string

  const theme = useTheme()
  const {
    customerId,
    deckId,
    equipmentId,
    locationId,
    siteId,
    setCustomerId,
    setDeckId,
    setDeckRevisionId,
    setEquipmentId,
    setLocationId,
    setScreenId,
    setSiteId,
    setDeckRevision
  } = useCurrentDeckContext()

  const equipment = observeEquipment()

  const filteredEquipment = () => {
    const filteredDecks = decks
      .filter((d) => d.CustomerId === customerId && d.SiteId === siteId && d.LocationId === locationId)
      .map((d) => d.DeckHeader.EquipmentId)

    let filteredEquipment = equipment.filter((e) => e.LocationId === locationId).map((e) => e.Name)

    filteredEquipment = filteredEquipment.concat(filteredDecks)

    const results = [...new Set(filteredEquipment)].sort()

    return results
  }

  function chooseCustomer(id) {
    if (setCustomerId(id)) {
      chooseSite(defaultString, true)
    }
    setOpenDropdown('customer') // Close all dropdowns except the current one
  }

  function chooseSite(id, confirm = false) {
    if (setSiteId(id, confirm)) {
      chooseLocation(defaultString, true)
    }
    setOpenDropdown('site')
  }

  function chooseLocation(id, confirm = false) {
    if (setLocationId(id, confirm)) {
      chooseEquipment(defaultString, true)
    }
    setOpenDropdown('location')
  }

  function chooseEquipment(id, confirm = false) {
    if (setEquipmentId(id, confirm)) {
      chooseDeck(defaultString, true)
    }
    setOpenDropdown('equipment')
  }

  function chooseDeck(id, confirm = false) {
    if (id.length) {
      const newDeck = decks.find((d) => d.id == id)
      const deckSet = setDeckId(id, confirm)
      if (deckSet) {
        const newScreen = screens.find((s) => s.id == newDeck.ScreenId)
        if (newScreen) {
          setScreenId(newScreen.id, deckSet)
        } else {
          setDeckRevisionId('')
          setDeckRevision({})
        }
      }
    } else {
      const deckSet = setDeckId(id, confirm)
      if (deckSet) {
        setScreenId(id)
        setDeckRevisionId(id)
        setDeckRevision({})
      }
    }
    setOpenDropdown('deck')
  }

  const StyledSelect = styled(Select)(() => ({
    '& .MuiSelect-outlined': {
      fontSize: '14px',
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.primary.contrastText,
      borderRadius: '2px'
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 0
    }
  }))

  return (
    <Box className="deck-selector">
      <StyledSelect
        value={customerId}
        onChange={(e) => chooseCustomer(e.target.value)}
        displayEmpty
        open={openDropdown === 'customer'}
        onOpen={() => setOpenDropdown('customer')}
        onClose={() => setOpenDropdown(null)}>
        <MenuItem value={-1} disabled={!siteId}>
          Select a Customer
        </MenuItem>
        {customers.map((c) => (
          <MenuItem key={c.id} value={c.id}>
            {c.Name}
          </MenuItem>
        ))}
      </StyledSelect>

      {customerId.length ? (
        <StyledSelect
          value={siteId}
          onChange={(e) => chooseSite(e.target.value)}
          displayEmpty
          open={openDropdown === 'site'}
          onOpen={() => setOpenDropdown('site')}
          onClose={() => setOpenDropdown(null)}>
          <MenuItem value={-1} disabled={!locationId}>
            Select a Site
          </MenuItem>
          {sites
            .filter((s) => s.CustomerId == customerId)
            .map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.Name}
              </MenuItem>
            ))}
        </StyledSelect>
      ) : null}

      {siteId.length ? (
        <StyledSelect
          value={locationId}
          onChange={(e) => chooseLocation(e.target.value)}
          displayEmpty
          open={openDropdown === 'location'}
          onOpen={() => setOpenDropdown('location')}
          onClose={() => setOpenDropdown(null)}>
          <MenuItem value={-1} disabled={!equipmentId}>
            Select a Location
          </MenuItem>
          {locations
            .filter((l) => l.SiteId == siteId)
            .map((l) => (
              <MenuItem key={l.id} value={l.id}>
                {l.Name}
              </MenuItem>
            ))}
        </StyledSelect>
      ) : null}

      {locationId && (
        <StyledSelect
          value={equipmentId && filteredEquipment().some((e) => e === equipmentId) ? equipmentId : ''}
          onChange={(e) => chooseEquipment(e.target.value)}
          displayEmpty
          open={openDropdown === 'equipment'}
          onOpen={() => setOpenDropdown('equipment')}
          onClose={() => setOpenDropdown(null)}
          renderValue={(selected) => selected || 'Select a Screen'}>
          <MenuItem value={-1} disabled={!equipmentId}>
            Select a Screen
          </MenuItem>
          {filteredEquipment().map((e) => (
            <MenuItem key={e} value={e}>
              {e}
            </MenuItem>
          ))}
        </StyledSelect>
      )}

      {equipmentId.length ? (
        <StyledSelect
          value={deckId}
          onChange={(e) => chooseDeck(e.target.value, true)}
          displayEmpty
          open={openDropdown === 'deck'}
          onOpen={() => setOpenDropdown('deck')}
          onClose={() => setOpenDropdown(null)}>
          <MenuItem value={-1} disabled={locationId}>
            Select a Deck
          </MenuItem>
          {decks
            .filter(
              (d) =>
                d.CustomerId == customerId &&
                d.SiteId == siteId &&
                d.LocationId == locationId &&
                d.DeckHeader.EquipmentId == equipmentId
            )
            .map((d) => (
              <MenuItem key={d.id} value={d.id}>{`${d.DeckHeader.DeckLevel} ${d.DeckHeader.MaterialNumber}`}</MenuItem>
            ))}
        </StyledSelect>
      ) : null}
    </Box>
  )
}

export default DeckSelector
